import React, { useState, useEffect, Suspense, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import ReportDrawer from '../components/ReportDrawer/ReportDrawer';
import AuditReport from '../components/AuditReport/AuditReport';
import LabControl from '../components/LabControl/LabControl';

const Lab = () => {
  const [searchParams] = useSearchParams();
  const scenarioId = searchParams.get('scenario');
  const view = searchParams.get('view') || 'problem';

  // 使用 state 来存储动态加载的组件和数据
  const [resources, setResources] = useState({
    Problem: null,
    Solved: null,
    reportData: null,
  });
  const [isReportOpen, setIsReportOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // 缓存React.lazy组件，避免重复创建
  const lazyComponentCache = useRef(new Map());

  useEffect(() => {
    if (!scenarioId) return;  

    setIsLoading(true);

    // 重置状态以显示加载中
    setResources({ Problem: null, Solved: null, reportData: null });

    const loadResources = async () => {
      try {
        // 获取或创建缓存的React.lazy组件
        const getOrCreateLazyComponent = (componentPath, fallback = null) => {
          const cacheKey = `${scenarioId}-${componentPath}`;
          
          if (lazyComponentCache.current.has(cacheKey)) {
            return lazyComponentCache.current.get(cacheKey);
          }
          
          const lazyComponent = React.lazy(() => 
            import(`../scenarios/${scenarioId}/${componentPath}`)
            .catch(() => {
              // 返回一个空函数组件而不是null，避免React.lazy错误
              return fallback || { default: () => null };
            })
          );
          
          lazyComponentCache.current.set(cacheKey, lazyComponent);
          return lazyComponent;
        };

        // 使用缓存的组件
        const ProblemComponent = getOrCreateLazyComponent('Problem.jsx');
        const SolvedComponent = getOrCreateLazyComponent('Solved.jsx');
        
        const reportDataModule = await import(`../scenarios/${scenarioId}/reportData.js`);

        setResources({
          Problem: ProblemComponent,
          Solved: SolvedComponent,
          reportData: reportDataModule.default,
        });
        setIsLoading(false);

      } catch (error) {
        console.error(`Failed to load resources for scenario ${scenarioId}:`, error);
        setIsLoading(false)
      }
    };

    loadResources();
  }, [scenarioId]);

  if (!scenarioId) {
    return (
      <div style={{ padding: '20px' }}>
        <h2>请在主页选择一个场景</h2>
        <a href="/">返回主菜单</a>
      </div>
    );
  }

  return (
    <div style={{ 
      background: 'radial-gradient(circle at center, #fdfdfd 0%, #f7f7f7 100%)',
      minHeight: '100vh'
    }}>
      {/* 唯一的 Suspense 用于处理所有动态加载的场景组件 */}
      <Suspense fallback={<div style={{ padding: '20px', fontSize: '24px' }}>加载场景中...</div>}>
        {view === 'problem' && resources.Problem && <resources.Problem />}
        {view === 'solved' && resources.Solved && <resources.Solved />}
      </Suspense>

      {/* 仅在资源加载后显示统一的UI控件 */}
      {(isLoading || resources.reportData) && (
        <LabControl
            scenarioId={scenarioId}
            currentView={view}
            hasSolvedVersion={!!resources.Solved}
            onToggleReport={() => setIsReportOpen(true)}
            isLoading={isLoading}
        />
      )}

      {/* 报告抽屉 */}
      {resources.reportData && (
        <ReportDrawer isOpen={isReportOpen} onClose={() => setIsReportOpen(false)}>
          <AuditReport {...resources.reportData} />
        </ReportDrawer>
      )}
    </div>
  );
};

export default Lab;